import { DateTime, Session } from '@benzinga/session';
import { CALENDAR_PAGESIZE, CALENDAR_SCREEN_SIZE, getSymbolsFromParameters } from './generateRestfulParams';
import { CalendarEvents, CalendarDataParameters, CalendarType, ExtremeDateValue, CalendarFeedId } from './entities';
import { getFirstLoadEvents } from './transform';
import { AutocompleteItem } from '@benzinga/autocomplete-manager';
import { createSymbol } from '@benzinga/autocomplete-manager';
import { setSymmetricDifference } from '@benzinga/utils';

enum AmendParam {
  dateEnd = 'dateEnd',
  dateStart = 'dateStart',
  symbols = 'symbols',
  watchlistIds = 'watchlistIds',
}

type RequestParametersApi = [EventsAction, CalendarDataParameters?, number?];

export enum EventsAction {
  addFirstBatch, // adds screen size batch
  addSymbols, // adds a symbol
  appendNew, // appends response events to the current events array
  doNothing,
  prependNew, // prepends response events to the current events array
  removeSymbols, // filters out a symbol
  replaceAll, // replaces current events with response ones
  slicePost, // slices off the events dated before new start date
  slicePre, // removes the events dated after new end date
}

export const NoRequestEventsActions = [
  EventsAction.doNothing,
  EventsAction.removeSymbols,
  EventsAction.slicePost,
  EventsAction.slicePre,
];

// calendarType: splits have date_announced instead of date
export type CalendarDateKey = 'date' | 'date_ex' | 'ex_dividend_date';

const replaceAllActions = (parameters: CalendarDataParameters): RequestParametersApi[] => [
  [EventsAction.addFirstBatch, parameters, CALENDAR_SCREEN_SIZE],
  [EventsAction.replaceAll],
];

export const getDateKey = (calendarType: CalendarType): CalendarDateKey => {
  switch (calendarType) {
    case 'splits':
      return 'date_ex';
    case 'dividends':
      return 'ex_dividend_date';
    default:
      return 'date';
  }
};

const getDateChangedParams = (
  oldParameters: CalendarDataParameters,
  newParameters: CalendarDataParameters,
  events: CalendarEvents[],
  hasMaxPagesize: boolean,
): RequestParametersApi[] => {
  const dateKey = getDateKey(newParameters.calendarType as CalendarType);
  const lastLoadedDateString = events[events.length - 1][dateKey] as string;
  const lastLoadedDateMoment = DateTime.fromISO(lastLoadedDateString);
  const newDateStartMoment = DateTime.fromISO(newParameters.dateStart);
  const oldDateStartMoment = DateTime.fromISO(oldParameters.dateStart ?? ExtremeDateValue.min);
  const newDateEndMoment = DateTime.fromISO(newParameters.dateEnd);
  const oldDateEndMoment = DateTime.fromISO(oldParameters.dateEnd);
  const relevantOldStartDateMoment: DateTime = hasMaxPagesize
    ? DateTime.fromISO(lastLoadedDateString).plus({ day: 1 })
    : oldDateStartMoment;

  let actions = [] as RequestParametersApi[];
  if (
    newDateStartMoment > oldDateEndMoment ||
    newDateEndMoment < relevantOldStartDateMoment ||
    relevantOldStartDateMoment > oldDateEndMoment
  ) {
    // there is no usable old data
    actions = replaceAllActions(newParameters);
  } else {
    let endEventIndex = 0;
    // dateEnd changed
    if (newDateEndMoment > oldDateEndMoment) {
      const dateStart = DateTime.fromISO(oldParameters.dateEnd).plus({ day: 1 }).toISODateString();

      actions.push([EventsAction.prependNew, { ...newParameters, dateStart }]);
    } else if (newDateEndMoment < oldDateEndMoment) {
      actions.push([EventsAction.slicePre]);
      endEventIndex = events.findIndex(event => DateTime.fromISO(event[dateKey] as string) <= newDateEndMoment);
    }

    // dateStart changed - adding another action
    if (hasMaxPagesize) {
      if (newDateStartMoment > lastLoadedDateMoment) {
        actions.push([EventsAction.slicePost]);
      } else if (endEventIndex) {
        // adding new if there the events number is reduced by slicePre
        const dateEnd = lastLoadedDateMoment.toISODateString();
        const startEventIndex = events.findIndex(event => event[dateKey] === lastLoadedDateString);
        actions.push([
          EventsAction.appendNew,
          { ...newParameters, dateEnd },
          CALENDAR_PAGESIZE - startEventIndex + endEventIndex,
        ]);
      }
    } else {
      if (newDateStartMoment > oldDateStartMoment) {
        actions.push([EventsAction.slicePost]);
      } else if (newParameters.dateStart !== oldParameters.dateStart) {
        const dateEnd = DateTime.fromISO(oldParameters.dateStart).plus({ day: -1 }).toISODateString();
        actions.push([
          EventsAction.appendNew,
          { ...newParameters, dateEnd },
          CALENDAR_PAGESIZE - events.length + endEventIndex,
        ]);
      }
    }
  }

  return actions.length ? actions : [[EventsAction.doNothing]];
};

const getSymbolsChangedParams = (
  oldParameters: CalendarDataParameters,
  newParameters: CalendarDataParameters,
  session: Session,
  hasMaxPagesize: boolean,
): RequestParametersApi[] => {
  const oldSymbols = getSymbolsFromParameters(oldParameters, session);
  const newSymbols = getSymbolsFromParameters(newParameters, session);

  const filters: AutocompleteItem[] = []; // added symbols
  let keptSymbolsNo = 0;
  newSymbols.forEach(symbol => {
    if (!oldSymbols.has(symbol)) {
      filters.push(createSymbol({ name: symbol, symbol, type: 'symbol' }));
    } else {
      keptSymbolsNo++;
    }
  });

  const hasMissingSymbols = oldSymbols.size !== keptSymbolsNo;
  let actions: RequestParametersApi[] = [];
  if ((hasMaxPagesize && hasMissingSymbols && keptSymbolsNo) || !keptSymbolsNo) {
    actions = replaceAllActions(newParameters);
  } else {
    if (hasMissingSymbols) {
      actions.push([EventsAction.removeSymbols]);
    }
    if (filters.length) {
      actions.push([EventsAction.addSymbols, { ...newParameters, filters, watchlistIds: [] }]);
    }
  }
  return actions.length ? actions : [[EventsAction.doNothing]];
};

export const getRequestParametersApi = (
  events: CalendarEvents[] | undefined,
  oldParameters: CalendarDataParameters | undefined,
  newParameters: CalendarDataParameters | undefined,
  session: Session,
): RequestParametersApi[] => {
  if (!newParameters) {
    return (events && events.length) || !oldParameters
      ? [[EventsAction.doNothing]]
      : [[EventsAction.addFirstBatch, oldParameters, CALENDAR_SCREEN_SIZE], [EventsAction.replaceAll]];
  }
  if (oldParameters) {
    if (
      oldParameters.calendarType !== newParameters.calendarType ||
      !events ||
      !events.length ||
      !newParameters[AmendParam.dateEnd] ||
      !oldParameters[AmendParam.dateEnd]
    ) {
      return replaceAllActions(newParameters);
    }
    let datesChanged = false;
    let symbolsChanged = false;
    if (
      oldParameters[AmendParam.dateEnd] !== newParameters[AmendParam.dateEnd] ||
      oldParameters[AmendParam.dateStart] !== newParameters[AmendParam.dateStart]
    ) {
      datesChanged = true;
    }
    if (
      setSymmetricDifference(
        getSymbolsFromParameters(oldParameters, session),
        getSymbolsFromParameters(newParameters, session),
      ).size > 0
    ) {
      symbolsChanged = true;
    }

    if (datesChanged && symbolsChanged) {
      return replaceAllActions(newParameters);
    }
    const hasMaxPagesize = events.length >= CALENDAR_PAGESIZE;
    if (datesChanged) {
      return getDateChangedParams(oldParameters, newParameters, events, hasMaxPagesize);
    }
    if (symbolsChanged) {
      return getSymbolsChangedParams(oldParameters, newParameters, session, hasMaxPagesize);
    }
  } else {
    return replaceAllActions(newParameters);
  }
  return [[EventsAction.doNothing]];
};

interface EventsData {
  events: CalendarEvents[];
  parameters: CalendarDataParameters;
}

export type ParametersMap = Map<CalendarFeedId, EventsData>;

export const getStartingParameters = (
  parametersMap: ParametersMap,
  newParameters: CalendarDataParameters,
  session: Session,
): EventsData => {
  let mapKey!: CalendarFeedId;
  let startingEventsSize = 0;
  parametersMap.forEach(({ events, parameters }, feedId) => {
    const actions = getRequestParametersApi(events, parameters, newParameters, session);
    const latestEventsSize = actions.reduce((eventsSum, action) => {
      eventsSum += getFirstLoadEvents(action[0], parameters, session, events, []).length;
      return eventsSum;
    }, 0);
    if (latestEventsSize > startingEventsSize) {
      mapKey = feedId;
      startingEventsSize = latestEventsSize;
    }
  });
  return parametersMap.get(mapKey) ?? ({} as EventsData);
};
